import { Component as ReactComponent, ComponentType, ComponentClass } from 'react'
import { generate } from 'randomstring'
import { Omit } from 'interfaces'
import * as omit from 'lodash/omit'

export type RegisterableDispatchProps = {
  register: (id: string) => void
  destroy: (id: string) => void
}

type RegisterableState = {
  id: string
  isMounted: boolean
}
type ChildComponentProps = {
  id: string
}

export type RegisterableOwnProps<TProps extends ChildComponentProps> = Omit<TProps, 'id'>

type RegisterableProps<TProps extends ChildComponentProps> =
  & RegisterableDispatchProps
  & RegisterableOwnProps<TProps>

const OMITTED_PROPS: (keyof RegisterableDispatchProps)[] = [
  'register', 'destroy',
]

abstract class RegisterableParent<TProps extends ChildComponentProps> extends ReactComponent<RegisterableProps<TProps>, RegisterableState> {
  constructor (props: RegisterableProps<TProps>, context: any) {
    super(props, context)
    this.state = {
      id: generate(),
      isMounted: false,
    }
    this.props.register(this.state.id)
  }

  componentDidMount () {
    this.setState({
      isMounted: true,
    })
  }

  componentWillUnmount () {
    this.props.destroy(this.state.id)
  }
}

function Registerable<TProps extends ChildComponentProps> (Component: ComponentType<TProps>): ComponentClass<RegisterableProps<TProps>> {
  return class extends RegisterableParent<TProps> {
    render () {
      if (!this.state.isMounted) {
        return null
      }
      const componentProps: RegisterableOwnProps<TProps> = omit(this.props, OMITTED_PROPS)

      const props: TProps = {
        ...componentProps,
        id: this.state.id,
      } as TProps

      return (
        <Component { ...props } />
      )
    }
  }
}

export default Registerable
